import type { Building } from "building-types/building/buildingTypes";
import type { PropInstance } from "building-types/props/propInstanceTypes";
import type { Prop, PropSrc } from "building-types/props/propTypes";
import { EntityInstanceType } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { EntityInstance } from "building-types/roomLayouts/roomLayoutTypes";
import type { RWTable } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import uid from "common/utils/uid";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { mergedPropLibraryNew } from "doc/propTools";
import { buildRoomLayoutInstancesByRoomLayoutId, entityInstancesForEntity } from "doc/rooms/layout/roomLayoutTools";
import { generateProp } from "../elements/generateProp";
import { RoomIndexImpl } from "../elements/rooms/roomIndexImpl";
import { copyEntityForComponentsWithOptions } from "../utils/componentInstanceUtils";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import { memoAsync } from "../utils/memo";

export function selectProps({
  doc: {
    assets,
    optionSets,
    instanceOptionSets,
    propInstances,
    roomLayouts,
    roomLayoutInstances,
    entityRoomLayouts,
    rooms: docRooms,
  },
  optionChoices: buildingOptionChoices,
  mirror,
  rooms,
  roomOutlineTable,
  roomLevelIndices,
  roomsByLevel,
  planarGraphs,
  roomFloorAssemblies,
  settings,
  walls,
  wallMasses,
  entityDisabledByGroup,
  enabledFeatures,
  relatedRoomsMap,
}: Pick<
  Building,
  | "doc"
  | "optionChoices"
  | "mirror"
  | "rooms"
  | "roomOutlineTable"
  | "roomLevelIndices"
  | "roomsByLevel"
  | "planarGraphs"
  | "roomFloorAssemblies"
  | "settings"
  | "walls"
  | "wallMasses"
  | "entityDisabledByGroup"
  | "enabledFeatures"
  | "relatedRoomsMap"
>) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    assets,
    docRooms,
    rooms,
    roomOutlineTable,
    roomLevelIndices,
    roomsByLevel,
    planarGraphs,
    roomFloorAssemblies,
    docPropInstances: propInstances,
    docRoomLayouts: roomLayouts,
    roomLayoutInstances,
    docEntityRoomLayouts: entityRoomLayouts,
    mirror,
    optionChoices: resolveOptionsInFormula(propInstances, definitions, buildingOptionChoices),
    rawOptionChoices: buildingOptionChoices,
    definitions,
    propCatalog: settings.props,
    walls,
    wallMasses,
    entityDisabledByGroup,
    componentsEnabled: enabledFeatures.modelComponents,
    relatedRoomsMap,
  };
}

const compare = makeGenerationStageComparator<typeof selectProps>({
  definitions: "shallow",
  docRooms: "shallow",
  optionChoices: "shallow",
  relatedRoomsMap: "shallow",
});

export async function generateProps(
  {
    assets,
    docRooms,
    docPropInstances,
    docRoomLayouts,
    roomLayoutInstances: activeRoomLayoutInstances,
    docEntityRoomLayouts,
    optionChoices,
    rawOptionChoices,
    definitions,
    rooms,
    roomOutlineTable,
    roomLevelIndices,
    roomsByLevel,
    planarGraphs,
    roomFloorAssemblies,
    mirror,
    walls,
    wallMasses,
    propCatalog,
    entityDisabledByGroup,
    componentsEnabled,
    relatedRoomsMap,
  }: ReturnType<typeof selectProps>,
  forEveryProp: ((propId: string, prop: Prop, disabled: boolean) => void) | null = null,
): Promise<{ props: RWTable<Prop> }> {
  const props: RWTable<Prop> = {};
  const wallLayerExtentsCache: WallLayerExtentsCache = {};
  const roomIndex = new RoomIndexImpl(rooms, roomLevelIndices, roomsByLevel, planarGraphs);

  const hasRoomLayouts = !!Object.keys(docEntityRoomLayouts).length;
  const roomLayoutInstancesByRoomLayoutId = hasRoomLayouts
    ? buildRoomLayoutInstancesByRoomLayoutId(activeRoomLayoutInstances)
    : undefined;
  const mergedPropLibrary = mergedPropLibraryNew(assets);

  for (const propInstanceId in docPropInstances) {
    const rawPropInstance = docPropInstances[propInstanceId];
    const propInstance = evalFormulas(rawPropInstance, (id) => optionChoices[id], undefined, entityDisabledByGroup);
    if (propInstance.disabled && !forEveryProp && !componentsEnabled) {
      continue;
    }

    const propInstances = entityInstancesForEntity({
      hasRoomLayouts,
      docEntityRoomLayouts,
      docRoomLayouts,
      activeRoomLayoutInstances: activeRoomLayoutInstances,
      roomLayoutInstancesByRoomLayoutId,
      entity: rawPropInstance,
      rooms: docRooms, // the original rooms referenced by room layouts and room layout instances
    });

    const allInstances: EntityInstance[] = [];
    const componentPropsByInstanceId = new Map<string, PropInstance>();
    // Populate allInstances and componentPropsByInstanceId with archetype (propInstances) and component instance placements
    copyEntityForComponentsWithOptions(
      rawPropInstance,
      relatedRoomsMap,
      propInstances,
      definitions,
      rawOptionChoices,
      allInstances,
      componentPropsByInstanceId,
      activeRoomLayoutInstances,
      !!forEveryProp,
    );

    for (const { entityInstanceType, parentId, edgeId, handleOverride, mirrored, instanceId } of allInstances) {
      if (!parentId) {
        continue;
      }

      const entityRoom = rooms[parentId];
      if (!entityRoom) {
        continue;
      }
      const level = entityRoom.level;
      const baseRoomId = entityRoom.sourceRoomId ?? parentId;
      const roomFloorAssembly = roomFloorAssemblies[baseRoomId] ?? roomFloorAssemblies[parentId];

      const {
        level: { id: levelId },
      } = entityRoom;

      const { wallIndex } = walls[levelId];

      assertIsNotNullOrUndefined(edgeId);

      const propInstanceSource = instanceId ? componentPropsByInstanceId.get(instanceId) ?? propInstance : propInstance;

      if (propInstanceSource.disabled && !forEveryProp) {
        continue;
      }

      const updatedProp: PropInstance = {
        ...propInstanceSource,
        parentId,
        edgeId,
        id: entityInstanceType === EntityInstanceType.ROOM_LAYOUT_INSTANCE ? uid() : propInstanceSource.id, // id is not used in casework (keep for later)
        entityInstanceType,
      };

      const propSrc: PropSrc = { src: updatedProp.propType, catalogType: updatedProp.catalogType };
      const propAttributes = propCatalog[propSrc.src];
      if (!propAttributes) {
        continue;
      }

      const newProp = await generateProp({
        mergedPropLibrary,
        propAttributes,
        level,
        roomFloorAssembly,
        propInstanceId: propInstanceId, // this is used for handle generation, so all instances of the same prop will have the same handle
        propInstance: updatedProp,
        room: entityRoom,
        roomOutlines: roomOutlineTable[entityRoom.id],
        roomIndex,
        wallIndex,
        mirror: mirror !== mirrored,
        wallMasses,
        wallLayerExtentsCache,
        handleOverride,
      });

      if (newProp) {
        if (forEveryProp && newProp.handle?.id) {
          forEveryProp(newProp.handle.id, newProp, !!propInstanceSource.disabled);
        }

        // Add prop to the parent room.
        props[newProp.id] = newProp;
      }
    }
  }

  return { props };
}

export default memoAsync(selectProps, generateProps, compare);
